import { AppLayout } from "app/core/layouts/AppLayout"
import React from "react"
import { Suspense, useState } from "react"
import { useCurrentUser } from "app/core/hooks/useCurrentUser"
import { useRouter } from "next/router"
import { useRouterQuery } from "@blitzjs/next"
import { useSession } from "@blitzjs/auth"
import { useQuery } from "@blitzjs/rpc"
import getOrgReport from "app/map-testing/queries/getOrganizationReport"
import * as Table from "reactabular-table"
import { flatten, groupBy } from "lodash"
import getOrderGroup from "app/orderGroups/queries/getOrderGroup"
import { Button } from "app/core/components/Button"
import getOrganization from "app/organizations/queries/getOrganization"

const Masthead = ({ onLoad, showAllTerms }) => {
  return (
    <section className="flex items-end border-b-2 border-black w-100 mb5 mt-6">
      <img src="/logo.png" className="mt3 mb3 mr2" style={{ height: 70 }} alt="" onLoad={onLoad} />
      <div className="mb3" style={{ marginLeft: "auto" }}>
        <div className="flex">
          <a
            href={`/api/map-testing/download?showAllTerms=${showAllTerms}`}
            target="_blank"
            rel="noreferrer noopener"
            className="px-4 py-2 ml-auto font-bold text-white no-underline rounded bg-brand-turquoise button mb4"
          >
            Download PDF
          </a>
        </div>
        <a href="https://homeschoolboss.com" className="link underline-hover black">
          HomeschoolBoss.com
        </a>{" "}
        • 651-272-5654
      </div>
    </section>
  )
}

const columns = [
  {
    property: "studentName",
    header: { label: "Student Name" },
  },
  {
    property: "grade",
    header: { label: "Grade" },
  },
  {
    property: "subject",
    header: { label: "Subject" },
  },
  {
    property: "ritScore",
    header: { label: "RIT Score" },
  },
  {
    property: "percentile",
    header: { label: "Percentile" },
  },
  {
    property: "season",
    header: { label: "Season" },
  },
]

const OrgReportTable = ({ resultSet, termName }) => {
  const resultsByGrade = groupBy(resultSet, (r) => r.data.Grade)
  return (
    <div className="mb-10">
      <h1 className="text-lg font-bold leading-6 text-gray-900 mb-8">
        NWEA MAP Growth Assessment Results
      </h1>
      <h2 className="text-lg font-bold leading-6 text-gray-900 mb-8">
        Season: {termName == "null" || !termName ? "Unknown" : termName}
      </h2>
      {Object.keys(resultsByGrade)
        .sort()
        .map((grade, i) => {
          const resultSet = resultsByGrade[grade]
          const resultsByStudent = groupBy(resultSet, (r) => r.student.id)
          const subjectResultsForEachStudent = Object.keys(resultsByStudent).map((studentId) => {
            const allTestResultsForStudent = resultsByStudent[studentId].map(
              (result) => result.data.testResults
            )
            return {
              studentId,
              // @ts-ignore
              student: resultsByStudent[studentId][0].student,
              // @ts-ignore
              data: resultsByStudent[studentId][0].data,
              testResults: groupBy(flatten(allTestResultsForStudent), (r) => r.TestName),
            }
          })

          const bestSubjectResultsForEachStudent: any[] = []

          subjectResultsForEachStudent.forEach((r, i) => {
            Object.keys(r.testResults).forEach((subject, j) => {
              const bestResult = r.testResults[subject].reduce((a, b) =>
                a.TestRITScore > b.TestRITScore ? a : b
              )

              const previousResult =
                bestSubjectResultsForEachStudent[bestSubjectResultsForEachStudent.length - 1]
              // This adds a space between students
              if (
                previousResult &&
                previousResult.studentId &&
                previousResult.studentId != r.studentId
              ) {
                bestSubjectResultsForEachStudent.push({
                  studentId: null,
                  reportId: null,
                  student: { firstName: "", lastName: "" },
                  testResults: {},
                  firstName: "",
                  lastName: "",
                  grade: null,
                  season: null,
                  subject: null,
                })
              }

              bestSubjectResultsForEachStudent.push({
                ...r,
                grade: r.data?.Grade,
                testResults: bestResult,
                season: termName,
              })
            })
          })

          return (
            <div id="test-results-report" key={i} className="mb-8">
              <h3 className="text-lg leading-6 text-gray-900">Grade: {grade}</h3>
              <Table.Provider columns={columns} className="table w-100 tests collapse mb3">
                <Table.Header className="orange tracked" />
                <Table.Body
                  rowKey="studentId"
                  className="ba b--light-silver tc"
                  rows={bestSubjectResultsForEachStudent.map((result) => {
                    return {
                      reportId: result.reportId,
                      studentId: result.student.id,
                      studentName: `${result.student.firstName} ${result.student.lastName}`,
                      grade: result.grade,
                      subject: result.testResults.TestName,
                      percentile: result.testResults.TestPercentile,
                      ritScore: result.testResults.TestRITScore,
                      season: result.season,
                    }
                  })}
                />
              </Table.Provider>
            </div>
          )
        })}
    </div>
  )
}

const OrgReportPage = () => {
  const queryParams = useRouterQuery()
  const [showAllTerms, setShowAllTerms] = useState(queryParams.showAllTerms === "true")
  return (
    <div
      className="relative px-6 mt-6"
      style={{
        overflow: "hidden",
        background: "white",
      }}
    >
      <Suspense fallback={<div />}>
        <Masthead onLoad={null} showAllTerms={showAllTerms} />
        <OrgReport setShowAllTerms={setShowAllTerms} showAllTerms={showAllTerms} />
      </Suspense>
    </div>
  )
}

const OrgReport = ({ setShowAllTerms, showAllTerms }) => {
  const currentUser = useCurrentUser()
  const session = useSession({ suspense: false })
  const router = useRouter()
  const orgId = session.activeOrgId ? session.activeOrgId : session.orgId!
  const organization = useQuery(getOrganization, { organizationId: orgId })
  const [testResults] = useQuery(getOrgReport, { orgId, showAllTerms })

  let orderGroupResults
  try {
    const results = useQuery(getOrderGroup, { where: { organizationId: orgId } })
    orderGroupResults = results[0]
  } catch (NotFoundError) {
    console.warn("No Order group found")
  }

  if (!currentUser || !organization[0] || !testResults) {
    return null
  }

  // @ts-ignore
  const resultsByTerm = groupBy(testResults.testResults, (t) => t.data?.TermName)
  const ordering = {}
  const sortOrder = ["Spring", "Winter", "Fall"]
  for (let i = 0; i < sortOrder.length; i++) ordering[sortOrder[i]] = i

  const sortedTermKeys = Object.keys(resultsByTerm).sort((termA, termB) => {
    const seasonA = termA.split(" ")
    const seasonB = termB.split(" ")
    const yearA = parseInt(termA.substr(termA.length - 4))
    const yearB = parseInt(termB.substr(termB.length - 4))
    return yearB - yearA || ordering[seasonA[0]] - ordering[seasonB[0]]
  })

  return (
    <div
      className="relative px-6 mt-6"
      style={{
        overflow: "hidden",
        background: "white",
      }}
      id="org-report"
    >
      <Suspense fallback={<div />}>
        <h1 className="text-2xl font-bold leading-6 text-gray-900 mb-8">
          <span>{organization[0].firstName}</span>
          <span> {organization[0].lastName}</span>
          {orderGroupResults && <span> - {orderGroupResults.name}</span>}
        </h1>
        <div className="mb-8">
          {testResults.availableTerms.length > 1 && (
            <Button
              onClick={(e) => {
                setShowAllTerms(!showAllTerms)
                router.push({
                  query: {
                    showAllTerms: !showAllTerms,
                  },
                })
              }}
            >
              {showAllTerms ? "Hide Past Seasons" : "Show All Seasons"}
            </Button>
          )}
        </div>
        <div>
          {testResults.testResults.length == 0 && <div>No test results available</div>}
          {sortedTermKeys.map((term, i) =>
            showAllTerms ? (
              <OrgReportTable key={i} termName={term} resultSet={resultsByTerm[term]} />
            ) : (
              i != 0 || <OrgReportTable key={i} termName={term} resultSet={resultsByTerm[term]} />
            )
          )}
        </div>
      </Suspense>
    </div>
  )
}

OrgReportPage.authenticate = true
OrgReportPage.getLayout = (page) => <AppLayout title="Map Testing Subscriptions">{page}</AppLayout>
export default OrgReportPage
